import {
  useGetIdentity,
  useGetList,
  useGetMany,
  useNotify,
  useRedirect,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo, useState } from "react";
import {
  Bell,
  CalendarClock,
  FileClock,
  Flag,
  Handshake,
  Inbox,
  ListChecks,
  StickyNote,
  TriangleAlert,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { isDealOpen } from "../deals/dealStatus";
import { findDealLabel, todayKey } from "../deals/dealUtils";
import {
  NEXT_STEP_TYPES,
  isDealRotting,
  offerExpiresSoon,
  silentDays,
} from "../deals/nextStep";
import { useTaskTypeLabel } from "../deals/NextStepFields";
import { useDealTasks } from "../deals/useDealTasks";
import { daysBetween } from "../misc/workdays";
import { addDaysKey } from "../misc/osloDate";
import { useCompleteFollowUp } from "../companies/useCompleteFollowUp";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useLeadAttention } from "./useLeadAttention";
import type {
  Company,
  CompanyNextStep,
  CompanyNote,
  Deal,
  MiminNotification,
  WorkItem,
} from "../types";

// «Å gjøre» (het «I dag», veikartets V9): én prioritert liste øverst på
// Oversikten som fletter gjøremålene dine — åpne oppgaver, neste steg og
// tilstander på muligheter, fornyelser innen 14 dager og notat-oppfølginger.
// Erstatter widgetene «Forfalte oppgaver», «Kommende oppfølginger» og
// «Kommende fornyelser». Oppfølgingsradaren og driftsradaren er kroniske
// tilstander (ikke datostyrt) og lever videre som egne kort under.
//
// ALLE ÅPNE OPPGAVER, IKKE BARE DAGENS. Lista viste bare oppgaver med frist i
// dag eller tidligere. Da var en oppgave med frist i morgen — eller uten frist
// i det hele tatt — usynlig på Oversikten, og «alt er à jour» kunne stå over
// en bunke ugjort arbeid.
//
// MINE ELLER TEAM (Mimin 1.5, US12). Lista starter i Mine for innlogget
// bruker; Team er et eksplisitt valg. Eierregelen i Mine: oppgaver der du er
// ansvarlig, muligheter (tilstander og fornyelse) du eier, og oppfølginger på
// notater du skrev. Oppgaver uten ansvarlig er teamarbeid og vises i Team,
// ikke som noens personlige gjøremål. Valget filtrerer bare visningen – det
// endrer aldri eierskap (bortsett fra «Ta denne», som er et uttrykt valg).
//
// GRUPPER (PRD 1.0 US12.3–12.4, pipeline v2.1 kap. 8.6). Datoene er
// kalenderdager i Europe/Oslo. Alt som krever handling står her, gruppert
// etter type: neste steg på muligheter (oppgaver med deal_id) har egne
// grupper for forfalt og i dag, og tilstandene på åpne muligheter — uten
// neste steg, råtnende, tilbud som utløper og leads uten eier — har hver sin.
// Resten fordeles på Forfalt, I dag, Kommende (de neste 14 dagene; oppgaver
// uansett frist) og Uten dato. Ingen kilde kuttes stille: hver spørring
// henter opptil 200 rader, og blir det flere, sier lista fra med totalen.
//
// En oppgave koblet til en mulighet vises én gang: som neste steg på
// muligheten, ikke i tillegg som vanlig oppgave. Revisit, anmeldelse og
// fakturering er ikke neste steg og står som vanlige oppgaver.
//
// Uten eier er en egen kø (US12.2): oppgaver uten ansvarlig, muligheter uten
// eier og neste steg uten ansvarlig.
const HORIZON_DAYS = 14;

type Scope = "mine" | "team" | "unowned";

// 200 er en grense mot en spørring som løper løpsk, ikke en
// produktbeslutning. Blir den nådd, vises totalen.
const MAKS_RADER = 200;

type Group =
  | "deal_overdue"
  | "overdue"
  | "deal_today"
  | "today"
  | "lead_parse_failed"
  | "leads_unowned"
  | "leads_no_contact"
  | "deal_no_step"
  | "deal_rotting"
  | "offer_expiring"
  | "upcoming"
  | "undated";

// Rekkefølgen er hastegraden: det som er forfalt først, så dagens, så
// tilstandene på mulighetene, og til slutt det som kommer.
const GROUPS: { key: Group; label: string }[] = [
  { key: "deal_overdue", label: "Forfalte neste steg" },
  { key: "overdue", label: "Forfalt" },
  { key: "deal_today", label: "Neste steg i dag" },
  { key: "today", label: "I dag" },
  { key: "lead_parse_failed", label: "Kunne ikke tolke skjemaet" },
  { key: "leads_unowned", label: "Leads uten eier" },
  { key: "leads_no_contact", label: "Leads uten kontakt" },
  { key: "deal_no_step", label: "Muligheter uten neste steg" },
  { key: "deal_rotting", label: "Råtnende muligheter" },
  { key: "offer_expiring", label: "Tilbud som utløper snart" },
  { key: "upcoming", label: "Kommende" },
  { key: "undated", label: "Uten dato" },
];

const OVERDUE_GROUPS: Group[] = ["deal_overdue", "overdue"];

type TodayItem = {
  id: string;
  kind:
    | "oppgave"
    | "kunde_steg"
    | "neste_steg"
    | "mulighet"
    | "fornyelse"
    | "oppfolging";
  /** Ikonmerkelappen; standard er typens navn (KIND_META). */
  kindLabel?: string;
  /**
   * YYYY-MM-DD, rangeringsnøkkel. null = uten dato: den er åpen og skal med,
   * men den er ikke forfalt og hører ikke hjemme mellom datoene. Den sorteres
   * sist og vises uten datomerke.
   */
  due: string | null;
  /** Fast gruppe for tilstander på muligheter; ellers avgjør datoen. */
  group?: Group;
  /** Tekst til høyre i stedet for datoen («Stille i 9 dager»). */
  note?: string;
  title: string;
  sub: string | null;
  path: string;
  /** Lead uten eier: raden får «Ta denne». */
  claim?: Deal;
  /** Notat-oppfølging: raden får «Fullfør oppfølging». */
  followUpNote?: CompanyNote;
};

const KIND_META: Record<
  TodayItem["kind"],
  {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    label: string;
  }
> = {
  oppgave: { icon: ListChecks, label: "Oppgave" },
  kunde_steg: { icon: Flag, label: "Neste steg" },
  neste_steg: { icon: Handshake, label: "Neste steg" },
  mulighet: { icon: Handshake, label: "Mulighet" },
  fornyelse: { icon: CalendarClock, label: "Fornyelse" },
  oppfolging: { icon: StickyNote, label: "Ring tilbake" },
};

const GROUP_ICON: Partial<
  Record<Group, React.ComponentType<React.SVGProps<SVGSVGElement>>>
> = {
  leads_unowned: Inbox,
  lead_parse_failed: TriangleAlert,
  leads_no_contact: Inbox,
  offer_expiring: FileClock,
};

// Kort tekstlig forhåndsvisning fra rik tekst (HTML) i kundenotater.
const preview = (html?: string | null) => {
  if (!html) return null;
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text ? (text.length > 70 ? `${text.slice(0, 70)}…` : text) : null;
};

// Neste steg er oppgaver på en mulighet av en neste steg-type (eller uten
// type). Revisit, anmeldelse og fakturering er noe annet.
const isDealStepTask = (t: WorkItem) =>
  t.deal_id != null &&
  (t.task_type == null || NEXT_STEP_TYPES.includes(t.task_type));

const NO_DEALS: Deal[] = [];
const NO_NOTIFICATIONS: MiminNotification[] = [];

// Hvor varselet leder: muligheten, ellers prosjektet. Avviste innsendinger
// (inntaket over grensen, til administrator) leder til loggen i
// Innstillinger.
const notificationPath = (n: MiminNotification) =>
  n.kind === "intake_rejected" && n.deal_id == null
    ? "/settings#intake-log"
    : n.kind === "error_log"
      ? "/settings#feillogg"
      : n.deal_id != null
        ? `/deals/${n.deal_id}/show`
        : n.project_id != null
          ? `/projects/${n.project_id}/show`
          : null;

export const TodayList = () => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const notify = useNotify();
  const [update, { isPending: claiming }] = useUpdate<Deal>();
  const followUp = useCompleteFollowUp();
  const { dealStages } = useConfigurationContext();
  const typeLabel = useTaskTypeLabel();
  const today = todayKey();
  const horizon = addDaysKey(today, HORIZON_DAYS);
  const { identity, isPending: identityPending } = useGetIdentity();
  const [scope, setScope] = useState<Scope>("mine");
  // Mine krever en identifisert bruker; uten identitet vises Team.
  const identified = identity?.id != null && identity.id !== "";
  const effectiveScope: Scope =
    identified || scope === "unowned" ? scope : "team";
  // Serverfilteret for eierfeltet i valgt kø.
  const own = (field: string) =>
    effectiveScope === "mine"
      ? { [field]: identity!.id }
      : effectiveScope === "unowned"
        ? { [`${field}@is`]: "null" }
        : {};
  // Samme regel på klienten (se kommentaren ved items under).
  const ownedBy = (value: unknown) =>
    effectiveScope === "team" ||
    (effectiveScope === "mine"
      ? String(value) === String(identity?.id)
      : value == null);
  const enabled = !identityPending;

  // Uleste varsler til deg (kap. 13.4: «Varsle teamet», tildeling og
  // duplikattreff). De er personlige og står øverst uansett Mine/Team.
  // Ingen lyd og ingen konfetti – bare en rad med «Marker som lest».
  const [markRead, { isPending: markingRead }] = useUpdate<MiminNotification>();
  const { data: notificationsRaw = NO_NOTIFICATIONS } =
    useGetList<MiminNotification>(
      "notifications",
      {
        filter: { recipient_id: identity?.id, "read_at@is": null },
        sort: { field: "created_at", order: "DESC" },
        pagination: { page: 1, perPage: 50 },
      },
      { enabled: enabled && identified },
    );
  // Et varsel markert som lest kommer tilbake i den bufrede lista med
  // read_at satt; det skal ikke vises.
  const notifications = identified
    ? notificationsRaw.filter(
        (n) => !n.read_at && String(n.recipient_id) === String(identity?.id),
      )
    : NO_NOTIFICATIONS;
  const markAsRead = (n: MiminNotification) =>
    markRead(
      "notifications",
      {
        id: n.id,
        data: { read_at: new Date().toISOString() },
        previousData: n,
      },
      {
        onError: (error) =>
          notify((error as Error)?.message || "ra.notification.http_error", {
            type: "error",
          }),
      },
    );

  // Alle åpne oppgaver — uansett frist, og også de uten frist.
  const {
    data: tasks = [],
    total: taskTotal,
    isPending: tasksPending,
    isError: tasksError,
    refetch: refetchTasks,
  } = useGetList<WorkItem>(
    "work_items",
    {
      filter: { "completed_at@is": "null", ...own("assignee_id") },
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: MAKS_RADER },
    },
    { enabled },
  );

  // Kundens neste steg (kundekortet). «Ingen planlagt steg» er ikke et
  // gjøremål og filtreres bort under.
  const {
    data: customerSteps = [],
    total: customerStepTotal,
    isPending: customerStepsPending,
    isError: customerStepsError,
    refetch: refetchCustomerSteps,
  } = useGetList<CompanyNextStep>(
    "company_next_steps",
    {
      filter: { "completed_at@is": "null", ...own("owner_id") },
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: MAKS_RADER },
    },
    { enabled },
  );

  // Åpne muligheter: grunnlaget for «uten neste steg», råtning, tilbud som
  // utløper og leads uten eier. deals.next_step_date leses ikke lenger —
  // neste steg er oppgaver (work_items.deal_id).
  const {
    data: openDealsRaw = NO_DEALS,
    total: openDealTotal,
    isPending: openDealsPending,
    isError: openDealsError,
    refetch: refetchOpenDeals,
  } = useGetList<Deal>(
    "deals",
    {
      filter: {
        "archived_at@is": null,
        status: "open",
        ...own("sales_id"),
      },
      sort: { field: "created_at", order: "ASC" },
      pagination: { page: 1, perPage: MAKS_RADER },
    },
    { enabled },
  );
  const openDeals = openDealsRaw.filter(
    (d) => !d.archived_at && isDealOpen(d) && ownedBy(d.sales_id),
  );
  // Neste steg per mulighet, uansett hvem oppgaven er tildelt: en mulighet
  // med et planlagt steg hos en kollega har et neste steg.
  const openDealIds = useMemo(
    () => openDealsRaw.map((d) => d.id),
    [openDealsRaw],
  );
  const { nextByDeal, isPending: dealTasksPending } = useDealTasks(openDealIds);
  // Leads som vises for alle (kap. 6.2 og 6.6, A4): skjema som ikke kunne
  // tolkes, og leads uten eier eller uten kontakt i over én virkedag.
  const leadAttention = useLeadAttention(enabled);

  // Navnet på muligheten til neste steg-oppgaver. De fleste står blant de
  // åpne mulighetene; resten (andres muligheter, i Mine) hentes her.
  const dealName = new Map(openDealsRaw.map((d) => [String(d.id), d.name]));
  const missingDealIds = [
    ...new Set(
      tasks
        .filter((t) => isDealStepTask(t) && !dealName.has(String(t.deal_id)))
        .map((t) => String(t.deal_id)),
    ),
  ];
  const { data: otherDeals = NO_DEALS } = useGetMany<Deal>(
    "deals",
    { ids: missingDealIds },
    { enabled: missingDealIds.length > 0 },
  );
  for (const d of otherDeals) dealName.set(String(d.id), d.name);

  // Fornyelser innen horisonten (inkludert forfalte).
  const {
    data: renewalDeals = [],
    total: renewalTotal,
    isPending: renewalsPending,
    isError: renewalsError,
    refetch: refetchRenewals,
  } = useGetList<Deal>(
    "deals",
    {
      filter: {
        "archived_at@is": "null",
        "renewal_date@lte": horizon,
        ...own("sales_id"),
      },
      sort: { field: "renewal_date", order: "ASC" },
      pagination: { page: 1, perPage: MAKS_RADER },
    },
    { enabled },
  );

  // Kundenotater med oppfølgingsdato til og med horisonten.
  const {
    data: followUpNotes = [],
    total: noteTotal,
    isPending: notesPending,
    isError: notesError,
    refetch: refetchNotes,
  } = useGetList<CompanyNote>(
    "company_notes",
    {
      filter: { "follow_up_date@lte": horizon, ...own("sales_id") },
      sort: { field: "follow_up_date", order: "ASC" },
      pagination: { page: 1, perPage: MAKS_RADER },
    },
    { enabled },
  );
  const companyIds = [
    ...new Set(
      [...followUpNotes, ...customerSteps].map((n) => String(n.company_id)),
    ),
  ];
  const {
    data: companies = [],
    isPending: companiesPending,
    isError: companiesError,
    refetch: refetchCompanies,
  } = useGetList<Company>(
    "companies",
    {
      filter: { "id@in": `(${companyIds.join(",")})` },
      sort: { field: "id", order: "ASC" },
      pagination: { page: 1, perPage: companyIds.length || 1 },
    },
    { enabled: companyIds.length > 0 },
  );
  const companyName = new Map(companies.map((c) => [String(c.id), c.name]));
  const hasError =
    tasksError ||
    customerStepsError ||
    openDealsError ||
    renewalsError ||
    notesError ||
    (companyIds.length > 0 && companiesError);
  const isLoading =
    tasksPending ||
    customerStepsPending ||
    openDealsPending ||
    dealTasksPending ||
    leadAttention.isPending ||
    renewalsPending ||
    notesPending ||
    (companyIds.length > 0 && companiesPending);
  const retry = () => {
    void Promise.all([
      refetchTasks(),
      refetchCustomerSteps(),
      refetchOpenDeals(),
      refetchRenewals(),
      refetchNotes(),
      ...(companyIds.length > 0 ? [refetchCompanies()] : []),
    ]);
  };

  // «Ta denne»: leaden blir din. Ingen e-post sendes; eierskiftet er alt.
  const claim = (deal: Deal) => {
    if (!identified) return;
    update(
      "deals",
      {
        id: deal.id,
        data: { sales_id: identity!.id },
        previousData: deal,
      },
      {
        onSuccess: () =>
          notify("crm.dashboard.today.claimed", {
            type: "success",
            messageArgs: { name: deal.name, _: `${deal.name} er din nå` },
          }),
        onError: (error) =>
          notify((error as Error)?.message || "ra.notification.http_error", {
            type: "error",
          }),
      },
    );
  };

  // «Fullfør oppfølging»: raden forsvinner, og fokus flyttes til raden som
  // tar plassen (eller den over, når det var den siste), så brukeren står
  // på samme sted i lista. Lista har egen rulling og monteres ikke på nytt.
  const completeFollowUp = (note: CompanyNote, button: HTMLElement) => {
    const row = button.closest("[data-today-row]");
    const neighbour =
      row?.nextElementSibling ?? row?.previousElementSibling ?? null;
    followUp.complete(note, () => {
      void refetchNotes();
      neighbour?.querySelector<HTMLElement>("button")?.focus({
        preventScroll: true,
      });
    });
  };

  // Kilder der serveren har flere rader enn vi hentet (US12.4).
  const capped = [
    {
      key: "tasks",
      shown: tasks.length,
      total: taskTotal,
      label: "åpne oppgaver",
      to: "/work_items",
    },
    {
      key: "customer_steps",
      shown: customerSteps.length,
      total: customerStepTotal,
      label: "neste steg",
      to: "/companies",
    },
    {
      key: "open_deals",
      shown: openDealsRaw.length,
      total: openDealTotal,
      label: "åpne muligheter",
      to: "/deals",
    },
    {
      key: "renewals",
      shown: renewalDeals.length,
      total: renewalTotal,
      label: "fornyelser",
      to: "/deals",
    },
    {
      key: "notes",
      shown: followUpNotes.length,
      total: noteTotal,
      label: "oppfølginger",
      to: "/companies",
    },
  ].filter((c) => (c.total ?? 0) > c.shown);

  const inboxStages = new Set(
    dealStages.filter((s) => s.is_inbox).map((s) => s.value),
  );
  const stageLabel = (deal: Deal) =>
    findDealLabel(dealStages, deal.stage) ?? deal.stage ?? null;
  const isUnownedLead = (deal: Deal) =>
    deal.sales_id == null && !!deal.stage && inboxStages.has(deal.stage);

  // Tilstandene på åpne muligheter. En mulighet kan stå i flere grupper
  // (råtnende OG tilbud som utløper): det er to ulike ting å gjøre.
  const dealItem = (
    deal: Deal,
    group: Group,
    extra: Partial<TodayItem> = {},
  ): TodayItem => ({
    id: `${group}:${deal.id}`,
    kind: "mulighet",
    due: null,
    group,
    title: deal.name,
    sub: stageLabel(deal),
    path: `/deals/${deal.id}/show`,
    ...extra,
  });
  // Leads uten eier i valgt kø, pluss de som har ventet over én virkedag
  // (de vises for alle).
  const unownedLeads = [
    ...openDeals.filter(isUnownedLead),
    ...leadAttention.unowned.filter(
      (d) => !openDeals.some((o) => String(o.id) === String(d.id)),
    ),
  ];
  const noStepDeals = openDeals.filter(
    (d) => !isUnownedLead(d) && !nextByDeal.has(String(d.id)),
  );
  const rottingDeals = openDeals
    .filter((d) => isDealRotting(d, dealStages, today))
    // Lengst stille øverst.
    .sort((a, b) => silentDays(b, today) - silentDays(a, today));
  const expiringOffers = openDeals.filter((d) => offerExpiresSoon(d, today));

  const offerNote = (validUntil: string) => {
    const days = daysBetween(today, validUntil);
    return days === 0
      ? translate("crm.dashboard.today.offer_expires_today", {
          _: "Tilbudet utløper i dag",
        })
      : translate("crm.dashboard.today.offer_expires_in", {
          smart_count: days,
          _: days === 1 ? "Utløper om 1 dag" : `Utløper om ${days} dager`,
        });
  };

  // Serverfiltrene gjentas på klienten. ra-core oppdaterer bufrede lister
  // med den endrede posten når en oppgave fullføres, en mulighet flyttes
  // eller en oppfølgingsdato tømmes — uten å hente lista på nytt. Posten blir
  // da liggende i «åpne oppgaver»-cachen med completed_at satt, og
  // Oversikten viste ferdige oppgaver til neste refetch.
  const items: TodayItem[] = [
    ...tasks
      .filter((t) => !t.completed_at && ownedBy(t.assignee_id))
      .map((t): TodayItem => {
        if (!isDealStepTask(t)) {
          return {
            id: `oppgave:${t.id}`,
            kind: "oppgave",
            // Ingen frist forblir null: den er ikke forfalt og hører ikke
            // hjemme mellom datoene.
            due: t.due_date ?? null,
            title: t.title,
            sub: null,
            path: `/work_items/${t.id}`,
          };
        }
        const name = dealName.get(String(t.deal_id));
        const due = t.due_date ?? null;
        return {
          id: `neste_steg:${t.id}`,
          kind: "neste_steg",
          kindLabel: t.task_type ? typeLabel(t.task_type) : undefined,
          due,
          // Forfalte og dagens neste steg har egne grupper; senere steg står
          // under Kommende sammen med resten.
          group:
            due && due < today
              ? "deal_overdue"
              : due === today
                ? "deal_today"
                : undefined,
          title: name ?? t.title,
          sub: name ? t.title : null,
          path: `/deals/${t.deal_id}/show`,
        };
      }),
    ...customerSteps
      .filter(
        (s) =>
          !s.completed_at &&
          !!s.description &&
          ownedBy(s.owner_id) &&
          (!s.due_date || s.due_date <= horizon),
      )
      .map(
        (s): TodayItem => ({
          id: `kunde_steg:${s.id}`,
          kind: "kunde_steg",
          due: s.due_date ?? null,
          title: companyName.get(String(s.company_id)) ?? "",
          sub: s.description ?? null,
          path: `/companies/${s.company_id}/show`,
        }),
      ),
    ...unownedLeads.map((d) =>
      dealItem(d, "leads_unowned", {
        kindLabel: translate("crm.dashboard.today.kind.lead", { _: "Lead" }),
        claim: d,
      }),
    ),
    ...leadAttention.parseFailed.map((d) =>
      dealItem(d, "lead_parse_failed", {
        kindLabel: translate("crm.dashboard.today.kind.lead", { _: "Lead" }),
        note: translate("crm.deals.lead.flag.parse_failed", {
          _: "Kunne ikke tolke skjemaet – sjekk",
        }),
      }),
    ),
    ...leadAttention.noContact.map((d) =>
      dealItem(d, "leads_no_contact", {
        kindLabel: translate("crm.dashboard.today.kind.lead", { _: "Lead" }),
        note: translate("crm.deals.lead.no_contact", {
          _: "Ingen kontakt logget",
        }),
      }),
    ),
    ...noStepDeals.map((d) =>
      dealItem(d, "deal_no_step", {
        note: translate("crm.dashboard.today.no_next_step", {
          _: "Ingen neste steg",
        }),
      }),
    ),
    ...rottingDeals.map((d) =>
      dealItem(d, "deal_rotting", {
        note: translate("crm.dashboard.today.silent_days", {
          smart_count: silentDays(d, today),
          _: `Stille i ${silentDays(d, today)} dager`,
        }),
      }),
    ),
    ...expiringOffers.map((d) =>
      dealItem(d, "offer_expiring", {
        due: d.offer_valid_until ?? null,
        note: offerNote(d.offer_valid_until!),
      }),
    ),
    ...renewalDeals
      .filter(
        (d) =>
          !d.archived_at &&
          d.renewal_date &&
          d.renewal_date <= horizon &&
          ownedBy(d.sales_id),
      )
      .map(
        (d): TodayItem => ({
          id: `fornyelse:${d.id}`,
          kind: "fornyelse",
          due: d.renewal_date ?? today,
          title: d.name,
          sub: null,
          path: `/deals/${d.id}/show`,
        }),
      ),
    ...followUpNotes
      .filter(
        (n) =>
          n.follow_up_date &&
          n.follow_up_date <= horizon &&
          ownedBy(n.sales_id),
      )
      .map(
        (n): TodayItem => ({
          id: `oppfolging:${n.id}`,
          kind: "oppfolging",
          due: n.follow_up_date ?? today,
          title: companyName.get(String(n.company_id)) ?? "",
          sub: preview(n.content),
          path: `/companies/${n.company_id}/show`,
          followUpNote: n,
        }),
      ),
  ]
    // Datert først, eldst øverst; udaterte sist. Sorteringen er stabil, så
    // rekkefølgen i gruppene uten dato (lengst stille først) står.
    .sort((a, b) => {
      if (a.due === b.due) return 0;
      if (a.due === null) return 1;
      if (b.due === null) return -1;
      return a.due.localeCompare(b.due);
    });

  const groupOf = (item: TodayItem): Group =>
    item.group ??
    (item.due === null
      ? "undated"
      : item.due < today
        ? "overdue"
        : item.due === today
          ? "today"
          : "upcoming");
  const grouped = GROUPS.map((g) => ({
    ...g,
    items: items.filter((item) => groupOf(item) === g.key),
  })).filter((g) => g.items.length > 0);

  // Datovisning: forfallsdatoen (gruppen sier allerede om den er i dag).
  const dueLabel = (due: string) =>
    due === today
      ? translate("crm.dashboard.today.due_today", { _: "I dag" })
      : new Date(`${due}T12:00:00`).toLocaleDateString("nb-NO", {
          day: "numeric",
          month: "short",
        });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="section-title">
          {translate("crm.dashboard.today.title", { _: "Å gjøre" })}
        </h2>
        {!hasError && !isLoading && items.length > 0 && (
          <Badge variant="secondary">{items.length}</Badge>
        )}
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={effectiveScope}
          onValueChange={(v) => v && setScope(v as Scope)}
          className="ml-auto"
          aria-label={translate("crm.dashboard.today.scope", {
            _: "Vis gjøremål for",
          })}
        >
          {identified && (
            <ToggleGroupItem value="mine" className="px-3">
              {translate("crm.dashboard.today.mine", { _: "Mine" })}
            </ToggleGroupItem>
          )}
          <ToggleGroupItem value="team" className="px-3">
            {translate("crm.dashboard.today.team", { _: "Team" })}
          </ToggleGroupItem>
          <ToggleGroupItem value="unowned" className="px-3">
            {translate("crm.dashboard.today.unowned", { _: "Uten eier" })}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {!hasError && !isLoading && capped.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {capped.map((c, i) => (
            <span key={c.key}>
              {i > 0 && " · "}
              {translate(`crm.dashboard.today.capped_${c.key}`, {
                shown: c.shown,
                total: c.total,
                _: `Viser ${c.shown} av ${c.total} ${c.label}`,
              })}{" "}
              <Link to={c.to} className="underline underline-offset-4">
                {translate("crm.dashboard.today.see_all", { _: "Se alle" })}
              </Link>
            </span>
          ))}
        </p>
      )}
      {/* Taket med intern rulling er prisen for å vise ALT som er åpent: uten
          det ville kortet strukket kolonnen så langt at widgetene ved siden av
          havnet utenfor skjermen. */}
      <Card className="max-h-[32rem] overflow-y-auto py-0">
        {hasError ? (
          <div
            role="alert"
            className="flex flex-col items-start gap-2 p-4 text-sm"
          >
            <p>Kunne ikke hente gjøremålene. Prøv igjen.</p>
            <button
              type="button"
              onClick={retry}
              className="font-medium text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Prøv igjen
            </button>
          </div>
        ) : isLoading ? (
          <div aria-label="Laster gjøremål" className="space-y-3 p-4">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : (
          <>
            {notifications.length > 0 && (
              <section aria-labelledby="today-group-notifications">
                <h3
                  id="today-group-notifications"
                  className="sticky top-0 z-10 flex items-center gap-2 border-b bg-card px-4 py-1.5 text-xs font-medium text-muted-foreground"
                >
                  {translate("crm.dashboard.today.group.notifications", {
                    _: "Varsler",
                  })}
                  <span className="font-normal">({notifications.length})</span>
                </h3>
                <div className="divide-y divide-border border-b">
                  {notifications.map((n) => {
                    const path = notificationPath(n);
                    return (
                      <div
                        key={n.id}
                        data-today-notification=""
                        className="flex min-h-12 items-center gap-3 px-4 py-2 text-sm"
                      >
                        <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          {path ? (
                            <Link
                              to={path}
                              className="hover:underline underline-offset-4"
                            >
                              {n.text}
                            </Link>
                          ) : (
                            n.text
                          )}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 shrink-0"
                          disabled={markingRead}
                          aria-label={translate(
                            "crm.dashboard.today.mark_read_named",
                            {
                              text: n.text,
                              _: `Marker som lest: ${n.text}`,
                            },
                          )}
                          onClick={() => markAsRead(n)}
                        >
                          {translate("crm.dashboard.today.mark_read", {
                            _: "Marker som lest",
                          })}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            {items.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                {effectiveScope === "mine"
                  ? translate("crm.dashboard.today.empty_mine", {
                      _: "Ingenting åpent på deg — velg Team for å se resten.",
                    })
                  : effectiveScope === "unowned"
                    ? translate("crm.dashboard.today.empty_unowned", {
                        _: "Ingenting åpent uten eier.",
                      })
                    : translate("crm.dashboard.today.empty", {
                        _: "Ingenting åpent — alt er à jour.",
                      })}
              </p>
            ) : (
              grouped.map((group) => (
                <section
                  key={group.key}
                  aria-labelledby={`today-group-${group.key}`}
                >
                  <h3
                    id={`today-group-${group.key}`}
                    className={`sticky top-0 z-10 flex items-center gap-2 border-b bg-card px-4 py-1.5 text-xs font-medium ${
                      OVERDUE_GROUPS.includes(group.key)
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {translate(`crm.dashboard.today.group.${group.key}`, {
                      _: group.label,
                    })}
                    <span className="font-normal">({group.items.length})</span>
                  </h3>
                  <div className="divide-y divide-border">
                    {group.items.map((item) => {
                      const meta = KIND_META[item.kind];
                      const Icon = GROUP_ICON[group.key] ?? meta.icon;
                      const overdue = OVERDUE_GROUPS.includes(group.key);
                      const row = (
                        <button
                          type="button"
                          onClick={() => redirect(item.path)}
                          className="flex min-h-12 min-w-0 flex-1 items-center gap-3 px-4 py-2 text-left text-sm transition-colors hover:bg-muted"
                        >
                          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {item.kindLabel ??
                              translate(
                                `crm.dashboard.today.kind.${item.kind}`,
                                {
                                  _: meta.label,
                                },
                              )}
                          </span>
                          <span className="min-w-0 flex-1 truncate">
                            <span className="font-medium">{item.title}</span>
                            {item.sub && (
                              <span className="text-muted-foreground">
                                {" "}
                                · {item.sub}
                              </span>
                            )}
                          </span>
                          {/* Tilstand i tekst; ellers datoen. Uten frist: ingen
                          dato, ingen merkelapp. */}
                          {item.note ? (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {item.note}
                            </span>
                          ) : item.due === null ? null : overdue ? (
                            <Badge variant="destructive">
                              {dueLabel(item.due)}
                            </Badge>
                          ) : (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {dueLabel(item.due)}
                            </span>
                          )}
                        </button>
                      );
                      // Raden og «Ta denne» er to knapper side om side — en
                      // knapp kan ikke ligge inne i en annen.
                      return (
                        <div
                          key={item.id}
                          data-today-row=""
                          className="flex items-center"
                        >
                          {row}
                          {item.claim && identified && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mr-4 h-8 shrink-0"
                              disabled={claiming}
                              aria-label={translate(
                                "crm.dashboard.today.claim_named",
                                {
                                  name: item.title,
                                  _: `Ta denne: ${item.title}`,
                                },
                              )}
                              onClick={() => claim(item.claim!)}
                            >
                              {translate("crm.dashboard.today.claim", {
                                _: "Ta denne",
                              })}
                            </Button>
                          )}
                          {item.followUpNote && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mr-4 h-8 shrink-0"
                              disabled={followUp.isPending(item.followUpNote)}
                              aria-label={translate(
                                "crm.dashboard.today.complete_follow_up_named",
                                {
                                  name: item.title,
                                  _: `Fullfør oppfølging: ${item.title}`,
                                },
                              )}
                              onClick={(event) =>
                                completeFollowUp(
                                  item.followUpNote!,
                                  event.currentTarget,
                                )
                              }
                            >
                              {translate(
                                "crm.company_notes.complete_follow_up",
                                {
                                  _: "Fullfør oppfølging",
                                },
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </Card>
    </div>
  );
};
